import { loadModal, loadAndShowModal, parseModalHtml } from "./loadModal";
import { ACCESS_REQUIREMENT_CODES } from "../helpers/accessRequirementErrors";

/**
 * Load and show any modal registered in the PHP growtype_modal_map filter.
 *
 * This is the one-stop helper for all map-backed modals. To add a new one:
 *   1. PHP: add_filter('growtype_modal_map', fn($m) => $m + ['MyModalId' => 'view.path']);
 *   2. JS:  loadMappedModal('MyModalId');
 *   Done — no new action, no new method.
 *
 * @param {string}   modalId  The modal element ID (e.g. 'growtypeChatStarterPackModal').
 * @param {Function} [cb]     Optional callback($modal) called after the modal is in the DOM.
 */
export function loadMappedModal(modalId, cb) {
  return loadModal(
    growtype_chat_ajax.get_mapped_modal_action,
    function ($modal) {
      // syncModalWithCurrentChat is handled by the show.bs.modal delegation
      // registered below (line ~104), which fires on the live DOM element.
      if (typeof cb === "function") cb($modal);

      // Re-initialize payment buttons (Apple Pay, Google Pay) on each show.
      if (typeof window.growtypeWcPaymentButton === "function") {
        setTimeout(() => window.growtypeWcPaymentButton(), 100);
      }

      // Use live DOM lookup instead of $modal[0] — loadModal wraps the raw
      // HTML string which may include leading text/comment nodes from PHP
      // templates, making $modal[0] a Comment node and crashing Bootstrap.
      const el = document.getElementById(modalId);
      if (!el) return;

      window.growtypeModal?.show(modalId);
    },
    { modalId: modalId, ajaxData: { modal_id: modalId } }
  );
}

function getCurrentChatCharacterImageUrl() {
  const profileGalleryImage = jQuery(".g-chatprofile-avatar a[href]")
    .first()
    .attr("href");

  if (profileGalleryImage) {
    return profileGalleryImage;
  }

  const profilePhotoBackgroundImage = jQuery(".g-chatprofile-photo-inner")
    .first()
    .css("background-image");

  if (profilePhotoBackgroundImage && profilePhotoBackgroundImage !== "none") {
    const matches = profilePhotoBackgroundImage.match(/url\((['"]?)(.*?)\1\)/);
    return matches?.[2] ?? "";
  }

  return "";
}

function getCurrentChatCharacterName() {
  const fullName = jQuery(".g-chatprofile-name .e-title").first().text().trim();
  return fullName.split(/\s+/).filter(Boolean)[0] ?? "";
}

function syncModalWithCurrentChat(modal, options = {}) {
  const { updateWaitingTitle = false } = options;
  const characterImageUrl = getCurrentChatCharacterImageUrl();
  const characterName = getCurrentChatCharacterName();
  const featuredImage = modal.find(".featured-img").first();

  if (
    featuredImage.length &&
    !featuredImage[0].style.backgroundImage &&
    characterImageUrl
  ) {
    featuredImage.css("background-image", `url('${characterImageUrl}')`);
  }

  if (characterName) {
    modal.attr("data-chat-character-name", characterName);
  }

  if (updateWaitingTitle && characterName) {
    // Don't overwrite the PHP-rendered title in roleplay sessions.
    if (modal.attr("data-is-roleplay") !== "1") {
      modal
        .find(".modal-title")
        .html(`Don't leave ${characterName} waiting...`);
    }
  }
}

function lockInstantChargeLink(link) {
  const $link = jQuery(link);

  if ($link.attr("data-processing") === "true") {
    return false;
  }

  if (!$link.attr("data-original-label")) {
    $link.attr("data-original-label", $link.text());
  }

  $link.attr("data-processing", "true");
  $link.addClass("disabled");
  $link.css({
    pointerEvents: "none",
    opacity: "0.65",
  });
  $link.text("Processing...");

  return true;
}

jQuery(document)
  .off("show.bs.modal.growtypeChatCharacterSync")
  .on(
    "show.bs.modal.growtypeChatCharacterSync",
    "#growtypeChatStarterPackModal, #growtypeChatImpulsePackModal",
    function () {
      syncModalWithCurrentChat(jQuery(this));
    }
  )
  .on(
    "show.bs.modal.growtypeChatCharacterSync",
    "#growtypeChatExtraCreditsModal",
    function () {
      syncModalWithCurrentChat(jQuery(this), { updateWaitingTitle: true });
    }
  )
  .off("click.growtypeChatInstantCharge")
  .on(
    "click.growtypeChatInstantCharge",
    '#growtypeChatExtraCreditsModal a[href*="action=gwc_charge_intent"], #growtypeChatStarterPackModal a[href*="action=gwc_charge_intent"], #growtypeChatImpulsePackModal a[href*="action=gwc_charge_intent"]',
    function (event) {
      const href = this.getAttribute("href");

      if (!href || !lockInstantChargeLink(this)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      window.location.assign(href);
    }
  );

/**
 * Inject the credits-shortfall message into a modal that has already been
 * inserted into the DOM. Used both by the inline-modal path (when the
 * backend sends modal HTML directly in the error response) and by the
 * loadMappedModal callback path.
 */
function injectCreditsShortfall(
  modal,
  requiredCredits,
  currentBalance,
  missingCreditsAmount
) {
  // Resolve the best target: dedicated span > first body paragraph > title.
  const shortfallEl = modal.find(".gwc-modal__credits-shortfall");
  const fallbackEl = modal.find(".modal-body p").first();
  const msgTarget = shortfallEl.length
    ? shortfallEl
    : fallbackEl.length
      ? fallbackEl
      : modal.find(".modal-title");

  // Cache the original title and subtitle text on first call.
  if (!window.growtype_chat_extra_credits_required_modal_title) {
    window.growtype_chat_extra_credits_required_modal_title = modal
      .find(".modal-title")
      .html();
  }
  if (!window.growtype_chat_extra_credits_required_modal_description) {
    window.growtype_chat_extra_credits_required_modal_description =
      msgTarget.html();
  }

  // Restore defaults so repeated opens don't show stale injected text.
  modal
    .find(".modal-title")
    .html(window.growtype_chat_extra_credits_required_modal_title);
  msgTarget.html(window.growtype_chat_extra_credits_required_modal_description);
  syncModalWithCurrentChat(modal, { updateWaitingTitle: true });

  // Override with the specific missing-credits message.
  if (requiredCredits && currentBalance !== undefined) {
    msgTarget.html(
      `This action requires <b class="highlighted">${requiredCredits} credits</b>, but you currently have <b class="highlighted">${currentBalance}</b>. Upgrade to Premium to get monthly free credits or top up below.`
    );
  } else if (missingCreditsAmount) {
    const text =
      missingCreditsAmount > 1
        ? `You're just ${missingCreditsAmount} credits away`
        : `You're just ${missingCreditsAmount} credit away`;
    msgTarget.html(text);
  }
}

export function displayModal(response) {
  // Support both raw jQuery XHR (error path, has .responseJSON)
  // and already-parsed JSON (success===false path, has .data directly).
  const data = response.responseJSON?.data ?? response.data;
  const message = data?.message ?? "";
  const missingCreditsAmount = data?.missing_credits_amount ?? "";
  const requiredCredits = data?.required_credits;
  const currentBalance = data?.current_balance;

  if (data?.modal) {
    const $modalHtml = parseModalHtml(data.modal);
    const modalId = $modalHtml.attr("id");
    if (modalId) {
      if (jQuery(`#${modalId}`).length === 0) {
        jQuery("body").append($modalHtml);
      } else {
        jQuery(`#${modalId}`).replaceWith($modalHtml);
      }

      // Re-initialize payment buttons if present in the new modal
      if (typeof window.growtypeWcPaymentButton === "function") {
        setTimeout(() => {
          window.growtypeWcPaymentButton();
        }, 100);
      }

      // Inject the missing-credits message into the inline modal before showing it.
      // This covers the code path where the backend sends modal HTML directly in
      // the error response (bypassing loadMappedModal).
      if (message === ACCESS_REQUIREMENT_CODES.EXTRA_CREDITS_REQUIRED) {
        injectCreditsShortfall(
          jQuery(`#${modalId}`),
          requiredCredits,
          currentBalance,
          missingCreditsAmount
        );
      }

      window.growtypeModal?.show(modalId);

      return;
    }
  }

  // Legacy message types: sent without data.modal HTML by older handlers.
  // Adding a new one = one line in the map, no new else-if branch needed.
  const legacyModalMap = {
    [ACCESS_REQUIREMENT_CODES.STARTER_REQUIRED]: "growtypeChatStarterPackModal",
    [ACCESS_REQUIREMENT_CODES.IMPULSE_REQUIRED]: "growtypeChatImpulsePackModal",
  };

  if (message === ACCESS_REQUIREMENT_CODES.PROFILE_REQUIRED) {
    window.growtypeModal?.show("growtypeFormAuthModal");
  } else if (legacyModalMap[message]) {
    loadMappedModal(legacyModalMap[message]);
  } else if (message === ACCESS_REQUIREMENT_CODES.SUBSCRIPTION_REQUIRED) {
    const modalTitle =
      response.responseJSON.data.modal_title ?? "Premium Feature";
    const unlockCredits = response.responseJSON.data.unlock_credits ?? null;
    const reason = response.responseJSON.data.reason ?? null;
    const feature = response.responseJSON.data.feature ?? null;
    const messageId = response.responseJSON.data.message_id ?? null;

    // If the modal is already in the DOM, update it immediately — no callback timing needed.
    if (modalTitle) {
      $("#growtypeWcSubscriptionModal .modal-title").text(modalTitle);
    }

    loadModal(
      growtype_chat_ajax.get_subscription_modal_action,
      function ($modal) {
        syncModalWithCurrentChat($modal);

        if (modalTitle) {
          $modal.find(".modal-title").text(modalTitle);
        }

        const creditsBlock = $modal.find(".b-unlockwith-credits");
        if (unlockCredits) {
          creditsBlock.show();
          creditsBlock.find(".credits-amount").text(unlockCredits);
          creditsBlock.find(".btn-growtype-chat-use-credits").attr(
            "data-params",
            JSON.stringify({
              credits_amount: unlockCredits,
              reason,
              feature,
            })
          );
        } else {
          creditsBlock.hide();
        }

        $modal.one("shown.bs.modal", function () {
          setTimeout(function () {
            const $slider = $modal.find(".gwc-benefits-slider");

            if (!$slider.length || !$slider.hasClass("slick-initialized")) return;

            // Navigate to the feature slide if a matching tag exists
            if (feature) {
              const $slide = $slider
                .find(".gwc-benefits-slider-slide:not(.slick-cloned)")
                .filter(function () {
                  const tags = ($(this).attr("data-tags") || "").split(" ");
                  return tags.includes(feature);
                });
              if ($slide.length) {
                const idx = parseInt($slide.attr("data-slick-index"), 10);
                if (!isNaN(idx)) {
                  $slider.slick("slickGoTo", idx, true);
                }
              }
            }

            // Disable autoplay — pause every video immediately
            $slider.find("video").each(function () {
              this.pause();
            });

            // Play video on the active slide, pause all others on slide change
            $slider.off("afterChange.videoManager").on("afterChange.videoManager", function () {
              $slider.find("video").each(function () { this.pause(); });
              $slider.find(".slick-active video").each(function () {
                try {
                  const playPromise = this.play();
                  if (playPromise && typeof playPromise.catch === "function") {
                    playPromise.catch(function () {});
                  }
                } catch (error) {
                  // A browser may reject playback synchronously.
                }
              });
            });
          }, 100);
        });

        window.growtypeModal?.show("growtypeWcSubscriptionModal");
      },
      {
        modalId: "growtypeWcSubscriptionModal",
        replace: true,
        ajaxData: {
          message_id: messageId,
        },
      }
    );
  } else if (message === ACCESS_REQUIREMENT_CODES.EXTRA_CREDITS_REQUIRED) {
    loadMappedModal("growtypeChatExtraCreditsModal", function (modal) {
      injectCreditsShortfall(
        modal,
        response.responseJSON?.data?.required_credits,
        response.responseJSON?.data?.current_balance,
        missingCreditsAmount
      );
    });
  } else if (message === "error" || message) {
    loadAndShowModal(
      growtype_chat_ajax.get_error_modal_action,
      "growtypeChatErrorModal",
      function ($modal) {
        if (message === "error") {
          if (window.growtype_chat_error_modal_original_message) {
            $modal
              .find(".modal-body")
              .html(window.growtype_chat_error_modal_original_message);
          }
        } else {
          if (!window.growtype_chat_error_modal_original_message) {
            window.growtype_chat_error_modal_original_message = $modal
              .find(".modal-body")
              .html();
          }
          $modal.find(".modal-body").html(message);
        }
      }
    );
  }
}

// Auto-show error modal if ?chat_error= is present in the URL
(function () {
  const params = new URLSearchParams(window.location.search);
  const chatError = params.get("chat_error");
  if (!chatError) return;

  jQuery(document).ready(function () {
    loadAndShowModal(
      growtype_chat_ajax.get_error_modal_action,
      "growtypeChatErrorModal",
      function ($modal) {
        if (!window.growtype_chat_error_modal_original_message) {
          window.growtype_chat_error_modal_original_message = $modal
            .find(".modal-body")
            .html();
        }
        $modal.find(".modal-body").html("<p>" + chatError + "</p>");
      }
    );
  });
})();
